'use client';

import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

function Input({ label, error, className, id, ...props }: InputProps) {
  const autoId = useId();
  const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : autoId);
  const errorId = `${inputId}-error`;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-ink-soft">
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={cn(
          'h-11 w-full rounded-xl border border-hairline bg-glass px-4 text-sm text-ink placeholder:text-ink-faint',
          'transition-colors hover:border-hairline-strong focus:border-emerald-500/60',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-coral-500/50',
          className
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...props}
      />
      {/* role=alert so the message is both programmatically tied to the field
          (aria-describedby) and announced the moment it appears (WCAG 3.3.1). */}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-coral-300">
          {error}
        </p>
      )}
    </div>
  );
}

export { Input };
